import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../lib/api";
import AppLayout from "../components/app/AppLayout";
import { Users, Plus, Trash2, Edit3, Save, X, Mail, Phone, MapPin, Hash } from "lucide-react";

const empty = { name: "", contact_name: "", email: "", phone: "", vat: "", address: "", notes: "" };

export default function CustomersPage() {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers");
      setCustomers(data || []);
    } catch (_e) { setCustomers([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(empty); setEditing("new"); setError(""); };
  const startEdit = (c) => { setForm({ ...empty, ...c }); setEditing(c.id); setError(""); };
  const cancel = () => { setEditing(null); setForm(empty); setError(""); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (editing === "new") await api.post("/customers", form);
      else await api.put(`/customers/${editing}`, form);
      cancel();
      await load();
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || t("customers.errSave"));
    }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm(t("customers.deleteConfirm"))) return;
    await api.delete(`/customers/${id}`);
    load();
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <AppLayout>
      <div className="px-8 py-8 max-w-6xl mx-auto" data-testid="customers-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="label-tech mb-2">{t("customers.label")}</div>
            <h1 className="font-display font-bold text-3xl text-slate-100 flex items-center gap-3"><Users size={26} className="text-blue-400"/> {t("customers.title")}</h1>
          </div>
          {!editing && (
            <button onClick={startNew} className="btn-primary px-5 py-3 rounded-md flex items-center gap-2" data-testid="new-customer-btn">
              <Plus size={16}/> {t("customers.newCustomer")}
            </button>
          )}
        </div>

        {editing && (
          <form onSubmit={save} className="tech-card p-6 mb-6 fade-in-up" data-testid="customer-form">
            <div className="font-display font-semibold text-slate-100 mb-4">
              {editing === "new" ? t("customers.newCustomer") : t("customers.editCustomer")}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={`${t("customers.name")} *`} required value={form.name} onChange={update("name")} testid="cust-name"/>
              <Field label={t("customers.contactName")} value={form.contact_name} onChange={update("contact_name")} testid="cust-contact"/>
              <Field label={t("customers.email")} type="email" value={form.email} onChange={update("email")} testid="cust-email"/>
              <Field label={t("customers.phone")} value={form.phone} onChange={update("phone")} testid="cust-phone"/>
              <Field label={t("customers.vat")} value={form.vat} onChange={update("vat")} testid="cust-vat"/>
              <Field label={t("customers.address")} value={form.address} onChange={update("address")} testid="cust-address"/>
              <div className="md:col-span-2">
                <label className="label-tech block mb-2">{t("customers.notes")}</label>
                <textarea rows={2} value={form.notes} onChange={update("notes")} className="w-full px-4 py-3 rounded-md text-sm" data-testid="cust-notes"/>
              </div>
            </div>
            {error && <div className="mt-3 px-3 py-2 rounded bg-red-500/10 border border-red-500/40 text-red-300 text-sm">{error}</div>}
            <div className="mt-5 flex items-center gap-3">
              <button type="submit" disabled={busy} className="btn-primary px-4 py-2 rounded-md flex items-center gap-2" data-testid="save-customer-btn">
                <Save size={14}/> {busy ? t("common.saving") : t("common.save")}
              </button>
              <button type="button" onClick={cancel} className="px-4 py-2 rounded-md text-sm bg-slate-800 text-slate-300 border border-slate-700 hover:border-slate-500 flex items-center gap-2">
                <X size={14}/> {t("common.cancel")}
              </button>
            </div>
          </form>
        )}

        <div className="tech-card overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700/60 flex items-center justify-between">
            <div className="label-tech">{t("customers.registry")}</div>
            <div className="text-xs text-slate-500 font-mono-data">{t("dashboard.recordCount", { n: customers.length })}</div>
          </div>
          {loading ? (
            <div className="p-8 text-slate-500">{t("common.loading")}</div>
          ) : customers.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center mb-3"><Users size={22} className="text-blue-400"/></div>
              <div className="font-display font-medium text-slate-100">{t("customers.empty")}</div>
              <div className="text-slate-500 text-sm mt-1">{t("customers.emptyHint")}</div>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {customers.map((c) => (
                <div key={c.id} className="px-6 py-4 flex items-start gap-4" data-testid={`customer-row-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-slate-100">{c.name}</div>
                    {c.contact_name && <div className="text-xs text-slate-500 mt-0.5">{t("customers.contactShort")} {c.contact_name}</div>}
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-400">
                      {c.email && <span className="flex items-center gap-1"><Mail size={12}/> {c.email}</span>}
                      {c.phone && <span className="flex items-center gap-1"><Phone size={12}/> {c.phone}</span>}
                      {c.vat && <span className="flex items-center gap-1 font-mono-data"><Hash size={12}/> {c.vat}</span>}
                      {c.address && <span className="flex items-center gap-1"><MapPin size={12}/> {c.address}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(c)} className="p-2 rounded text-slate-400 hover:text-blue-400 hover:bg-blue-500/10" data-testid={`edit-customer-${c.id}`}><Edit3 size={16}/></button>
                    <button onClick={() => remove(c.id)} className="p-2 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/10" data-testid={`delete-customer-${c.id}`}><Trash2 size={16}/></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

function Field({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <div>
      <label className="label-tech block mb-2">{label}</label>
      <input type={type} value={value || ""} onChange={onChange} required={required} className="w-full px-4 py-3 rounded-md text-sm" data-testid={testid}/>
    </div>
  );
}
